/**
 * 统一 API 客户端。
 *
 * - `VITE_API_BASE` 为空时走同源（本地 vite 代理 /api → 127.0.0.1:8787；线上 Pages 用 _routes/Worker 或直接配 API 域名）。
 * - 所有响应都是 JSON；非 2xx 会抛 {@link ApiError}（带 code，便于页面区分 adult_mode_off 之类）。
 * - 后台 token 由调用方从 session 里取（见 lib/session.ts），这里只负责塞 Authorization 头。
 */
import type {
  Card,
  CardsFile,
  DanmakuFile,
  DanmakuItem,
  PlatformSettings,
  RoomPublicState,
  SiteGame,
  StatsOverview,
  Tier,
} from "@baibaisex/shared";

export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";

/**
 * 图片地址归一化：本站存储的图在 API 域名下（`/api/media/...`），
 * 而 `/covers/x.svg` 这类是前端自己的静态资源 —— 只有前者需要补 API 前缀。
 */
export function imageSrc(url: string | null | undefined): string {
  const value = (url ?? "").trim();
  if (!value) return "";
  if (value.startsWith("/api/")) return `${API_BASE}${value}`;
  return value;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** 把 ApiError.code 翻译成人话，页面统一用它显示错误 */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "adult_mode_off") return "成人模式没开，这一档现在不可用（后台可开）";
    if (error.code === "empty_pool") return "这个卡池还没有内容";
    if (error.code === "network") return error.message;
    return error.message;
  }
  return error instanceof Error ? error.message : "出错了";
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, token, signal } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch {
    throw new ApiError(0, "network", "连不上服务器，检查 API 地址或网络");
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const payload = (data ?? {}) as { error?: string; code?: string };
    throw new ApiError(res.status, payload.code ?? "http_error", payload.error ?? `请求失败（${res.status}）`);
  }
  return data as T;
}

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

/* ------------------------------------------------------------------ 公开接口 */

export interface PublicSettings {
  ageGate: boolean;
  adultMode: boolean;
  skipDailyLimit: number;
  announcement: string;
  announcementEnabled: boolean;
  handicapAllowed: boolean;
  profile: PlatformSettings["profile"];
}

/** 站点配置（公开只读）：后台「设置」里改的东西，前台靠它渲染 */
export interface SiteConfigData {
  /** 设置版本戳（ms）：前台本地缓存的 key，变了才换渲染内容 */
  updatedAt: number;
  settings: PublicSettings;
  /** 游戏大厅列表（后台「游戏管理」里配的，只含启用项） */
  games: SiteGame[];
  tiers: Record<string, string>;
  /** 后台上传图片的体积上限与当前存储后端（提示文案用） */
  mediaMaxBytes: number;
  mediaBackend: string;
}

export interface BootstrapData extends SiteConfigData {
  seeded: boolean;
  /** 首页「模拟上贡」累计次数 */
  tributes: number;
}

export interface MediaItem {
  id: string;
  url: string;
  path: string;
  mime: string;
  bytes: number;
  createdAt: number;
}

export interface MediaLibrary {
  backend: "r2" | "d1";
  backendLabel: string;
  /** 当前后端真正的上限 */
  maxBytes: number;
  /** 前端压缩后的目标上限（两个后端都满足） */
  recommendedBytes: number;
  items: MediaItem[];
}

export interface DrawResponse {
  card: Card;
  tier: Tier;
  poolSize: number;
}

/**
 * 站点配置的协商缓存：
 * - 带上本地 updatedAt 时发 `?since=` 与 `If-None-Match`；
 * - 服务端没变会回 304，这里翻译成 `{ notModified: true }` 而不是当错误抛。
 */
async function getSiteConfig(since?: number): Promise<{ notModified: boolean; data: SiteConfigData | null }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/site-config${qs({ since })}`, {
      headers: since ? { "if-none-match": `"cfg-${since}"` } : {},
    });
  } catch {
    throw new ApiError(0, "network", "连不上服务器，检查 API 地址或网络");
  }
  if (res.status === 304) return { notModified: true, data: null };
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const payload = (data ?? {}) as { error?: string; code?: string };
    throw new ApiError(res.status, payload.code ?? "http_error", payload.error ?? `请求失败（${res.status}）`);
  }
  return { notModified: false, data: data as SiteConfigData };
}

export const api = {
  bootstrap: () => request<BootstrapData>("/api/bootstrap"),
  /** 站点配置：`since` 传本地缓存的 updatedAt，命中则 notModified=true */
  siteConfig: getSiteConfig,
  visit: () => request<{ ok?: boolean }>("/api/visit", { method: "POST" }),
  /** 首页「模拟上贡」：服务端计数 +1，返回最新累计值（3 秒内重复点击会返回 throttled） */
  tribute: () => request<{ tributes: number; throttled?: boolean }>("/api/tribute", { method: "POST" }),

  listCards: (tier: Tier, tags: string[] = []) =>
    request<{ tier: Tier; cards: Card[] }>(`/api/cards${qs({ tier, tags: tags.join(",") })}`),
  drawCard: (payload: { tier: Tier; tags?: string[]; recent?: string[] }) =>
    request<DrawResponse>("/api/cards/draw", { method: "POST", body: payload }),
  getCard: (id: string) => request<{ card: Card }>(`/api/cards/${encodeURIComponent(id)}`),

  listDanmaku: (limit = 60) => request<{ items: DanmakuItem[] }>(`/api/danmaku${qs({ limit })}`),
  drawDanmaku: () => request<{ item: DanmakuItem }>("/api/danmaku/draw", { method: "POST" }),

  report: (payload: { targetType: "card" | "danmaku" | "room" | "user"; targetId: string; reason: string; detail?: string }) =>
    request<{ ok: true; id: string }>("/api/reports", { method: "POST", body: payload }),

  seed: (force = false) => request<Record<string, unknown>>("/api/seed", { method: "POST", body: { force } }),

  /* ------------------------------------------------------------ 房间（双人） */

  createRoom: (name: string) =>
    request<{ code: string; token: string; seat: 0 }>("/api/rooms", { method: "POST", body: { name } }),
  joinRoom: (code: string, name: string) =>
    request<{ code: string; token: string; seat: 1 }>(`/api/rooms/${encodeURIComponent(code)}/join`, {
      method: "POST",
      body: { name },
    }),
  roomState: (code: string, token?: string | null) =>
    request<RoomPublicState>(`/api/rooms/${encodeURIComponent(code)}${qs({ token })}`),
  /** WS 地址：Durable Object 在 upgrade 时就要用 token 认座位，所以必须挂 query（否则 401） */
  roomSocketUrl: (code: string, token?: string) => {
    const base = API_BASE || window.location.origin;
    const url = new URL(`${base}/api/rooms/${encodeURIComponent(code)}/ws`);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    if (token) url.searchParams.set("token", token);
    return url.toString();
  },

  /* ------------------------------------------------------------ 后台管理 */

  adminLogin: (username: string, password: string) =>
    request<{ token: string; expiresAt: number; admin: { id: string; username: string } }>("/api/admin/login", {
      method: "POST",
      body: { username, password },
    }),
  adminLogout: (token: string) => request<{ ok?: boolean }>("/api/admin/logout", { method: "POST", token }),
  adminMe: (token: string) => request<{ admin: { id: string; username: string } }>("/api/admin/me", { token }),

  adminSettings: (token: string) => request<{ settings: PlatformSettings }>("/api/admin/settings", { token }),
  /** 保存设置：`warnings` 是敏感词体检给的提醒（命中 mask 词不阻断保存） */
  adminPatchSettings: (token: string, patch: Partial<PlatformSettings>) =>
    request<{ settings: PlatformSettings; warnings?: string[] }>("/api/admin/settings", { method: "PATCH", body: patch, token }),

  /* ------------------------------------------------------------ 图片上传 */

  adminMediaLibrary: (token: string) => request<MediaLibrary>("/api/admin/media", { token }),
  adminUploadMedia: (token: string, dataUrl: string) =>
    request<{ ok: true; id: string; url: string; path: string; mime: string; bytes: number }>("/api/admin/media", {
      method: "POST",
      body: { dataUrl },
      token,
    }),
  adminDeleteMedia: (token: string, id: string) =>
    request<{ id: string }>(`/api/admin/media/${encodeURIComponent(id)}`, { method: "DELETE", token }),

  adminCards: (token: string, all = false) => request<{ file: CardsFile }>(`/api/admin/cards${qs({ all: all ? 1 : undefined })}`, { token }),
  adminSaveCard: (token: string, card: Card & { packId: string; packName: string; tier: Tier; enabled: boolean }) =>
    request<{ ok: true; id: string }>("/api/admin/cards", { method: "POST", body: card, token }),
  adminDeleteCard: (token: string, id: string) =>
    request<{ ok?: boolean }>(`/api/admin/cards/${encodeURIComponent(id)}`, { method: "DELETE", token }),

  adminDanmaku: (token: string, all = false) =>
    request<{ file: DanmakuFile }>(`/api/admin/danmaku${qs({ all: all ? 1 : undefined })}`, { token }),
  adminSaveDanmaku: (token: string, item: DanmakuItem & { tier: Tier; enabled: boolean }) =>
    request<{ ok: true; id: string }>("/api/admin/danmaku", { method: "POST", body: item, token }),
  adminDeleteDanmaku: (token: string, id: string) =>
    request<{ ok?: boolean }>(`/api/admin/danmaku/${encodeURIComponent(id)}`, { method: "DELETE", token }),

  adminExport: (token: string) => request<ExportBundle>("/api/admin/export", { token }),
  adminImport: (token: string, kind: "cards" | "danmaku", mode: "replace" | "merge", payload: unknown) =>
    request<Record<string, unknown>>(`/api/admin/import/${kind}`, { method: "POST", body: { mode, payload }, token }),

  adminWords: (token: string) =>
    request<{ words: { word: string; severity: "mask" | "block" }[] }>("/api/admin/sensitive-words", { token }),
  adminReplaceWords: (token: string, words: { word: string; severity: "mask" | "block" }[], mode: "replace" | "merge" = "replace") =>
    request<{ ok?: boolean; words?: unknown }>("/api/admin/sensitive-words", { method: "PUT", body: { mode, words }, token }),
  adminDeleteWord: (token: string, word: string) =>
    request<{ ok?: boolean }>(`/api/admin/sensitive-words/${encodeURIComponent(word)}`, { method: "DELETE", token }),

  adminRooms: (token: string, params: { status?: string; live?: boolean } = {}) =>
    request<{ rooms: AdminRoomRow[]; now: number }>(`/api/admin/rooms${qs({ status: params.status, live: params.live ? 1 : undefined })}`, { token }),
  adminCloseRoom: (token: string, code: string) =>
    request<{ ok?: boolean }>(`/api/admin/rooms/${encodeURIComponent(code)}/close`, { method: "POST", token }),

  adminReports: (token: string, status?: string) =>
    request<{ reports: AdminReportRow[] }>(`/api/admin/reports${qs({ status })}`, { token }),
  adminPatchReport: (token: string, id: string, status: "open" | "handled" | "rejected") =>
    request<{ id: string; status: string }>(`/api/admin/reports/${encodeURIComponent(id)}`, { method: "PATCH", body: { status }, token }),

  adminBans: (token: string) => request<{ bans: AdminBanRow[] }>("/api/admin/bans", { token }),
  adminAddBan: (token: string, ban: { kind: "room" | "user" | "word"; value: string; note?: string }) =>
    request<{ ok: true; id: string }>("/api/admin/bans", { method: "POST", body: ban, token }),
  adminDeleteBan: (token: string, id: string) =>
    request<{ ok?: boolean }>(`/api/admin/bans/${encodeURIComponent(id)}`, { method: "DELETE", token }),

  adminStats: (token: string, days = 14) =>
    request<{ stats: StatsOverview; openRooms: number; days: number }>(`/api/admin/stats${qs({ days })}`, { token }),
  adminSeed: (token: string, force = false) =>
    request<Record<string, unknown>>("/api/admin/seed", { method: "POST", body: { force }, token }),
};

export interface ExportBundle {
  exportedAt: string;
  cards: CardsFile;
  danmaku: DanmakuFile;
  sensitiveWords: { version: number; words: { word: string; severity: "mask" | "block" }[] };
  settings: PlatformSettings;
}

export interface AdminRoomRow {
  code: string;
  host_name: string | null;
  guest_name: string | null;
  status: string;
  seats: number;
  created_at: number;
  updated_at: number;
  closed_at: number | null;
}

export interface AdminReportRow {
  id: string;
  target_type: string;
  target_id: string;
  reason: string;
  detail: string | null;
  status: string;
  created_at: number;
}

export interface AdminBanRow {
  id: string;
  kind: string;
  value: string;
  note: string | null;
  created_at: number;
}
